"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWRInfinite from "swr/infinite";
import {
  ContestCategoriesResponse,
  ContestCategoryAPI,
  ContestCategoryGetRequest,
} from "@/restapi/contest/category";

type Payload = {
  query?: string;
  perPage?: number;
  isActive?: boolean;
};

const DEFAULT_PER_PAGE = 20;

export const useContestCategoryInfenityScroll = (request: Payload) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const perPage = request.perPage ?? DEFAULT_PER_PAGE;

  // 検索条件ごとにキャッシュが分かれるようにキー用のparamsを安定化
  const stableParams = useMemo(() => {
    return {
      q: request.query ?? "",
      per_page: String(perPage),
      is_active: request.isActive === undefined ? "" : String(request.isActive),
    };
  }, [request.query, request.isActive, perPage]);

  const getKey = (index: number, previousPageData: ContestCategoriesResponse | null): string | null => {
    if (previousPageData && previousPageData.meta.currentPage >= previousPageData.meta.lastPage) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(index + 1),
      ...stableParams,
    });

    // q/is_active/per_page が入った “検索条件込みキー”
    return `contest-category-index?${params.toString()}`;
  };

  const { data, error, setSize, isValidating } = useSWRInfinite<ContestCategoriesResponse>(
    getKey,
    async (key: string) => {
      const url = new URL(key, "http://dummy.local");
      const page = Number(url.searchParams.get("page") ?? 1);

      const q = url.searchParams.get("q") || undefined;

      const isActiveRaw = url.searchParams.get("is_active");
      const isActive =
        isActiveRaw === "" || isActiveRaw === null ? undefined : isActiveRaw === "true";

      const perPage = Number(url.searchParams.get("per_page") ?? DEFAULT_PER_PAGE) as 20 | 50 | 100;

      const req: ContestCategoryGetRequest = {
        page,
        perPage,
        query: q,
        isActive,
      };

      return ContestCategoryAPI.get(req);
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0,
    }
  );

  // 初回ロード判定（SWRInfinite は isLoading を返さないため
  // !data && !error で初回取得中を判定）
  const isLoading = !data && !error;

  const computedItems = useMemo(() => {
    return data ? data.flatMap((page) => page.data) : [];
  }, [data]);

  // フェッチ中に data が undefined になっても items を空にしない（アンマウント対策）
  const cachedItemsRef = useRef<typeof computedItems>([]);
  useEffect(() => {
    // 0件検索を許容したいので「length > 0」の時だけキャッシュ更新はやめる
    // 検索結果が0件の時も正しく0件表示したいので「dataが来たら」更新にする
    if (data) {
      cachedItemsRef.current = computedItems;
    }
  }, [data, computedItems]);

  const items = data ? computedItems : cachedItemsRef.current;

  const last = data?.[data.length - 1];
  const hasMore = last ? last.meta.currentPage < last.meta.lastPage : false;

  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingMore || isValidating) return;

    setIsFetchingMore(true);
    Promise.resolve(setSize((s) => s + 1)).finally(() => setIsFetchingMore(false));
  }, [hasMore, isFetchingMore, isValidating, setSize]);

  const reset = useCallback(() => {
    cachedItemsRef.current = [];
    setSize(1);
  }, [setSize]);

  return {
    items,
    isLoading,
    isError: !!error,
    loadMore,
    hasMore,
    isFetchingMore,
    isValidating,
    reset,
  };
};